import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Download,
  CheckCircle2,
  X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import { getExampleBySlug } from "@/data/examples";

const SITE_URL = "https://episodekit.net";

const useJsonLd = (jsonLd) => {
  useEffect(() => {
    if (!jsonLd) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-jsonld", "example-detail");
    el.text = JSON.stringify(jsonLd);
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [JSON.stringify(jsonLd)]);
};

export default function EpisodeExampleDetail() {
  const { slug } = useParams();
  const example = getExampleBySlug(slug);
  const [lightbox, setLightbox] = useState(null);

  const seoTitle = example?.seo?.title || "Episode Example | EpisodeKit";
  const seoDescription =
    example?.seo?.description ||
    "Real episode output example generated by EpisodeKit.";
  const seoPath = example
    ? `/episode-examples/${example.slug}`
    : "/episode-examples";

  useSEO({
    title: seoTitle,
    description: seoDescription,
    path: seoPath,
    image: example?.seo?.ogImage,
  });

  useJsonLd(
    example
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Examples",
              item: `${SITE_URL}/episode-examples`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: example.title,
              item: `${SITE_URL}/episode-examples/${example.slug}`,
            },
          ],
        }
      : null,
  );

  if (!example) return <Navigate to="/episode-examples" replace />;

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid={`example-detail-${example.slug}`}
    >
      <Navbar />

      <main className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-neutral-500 mb-8 flex items-center gap-2"
          >
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span aria-hidden="true">/</span>
            <Link
              to="/episode-examples"
              className="hover:text-white transition-colors"
            >
              Examples
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-neutral-300 truncate max-w-[60vw]">
              {example.title}
            </span>
          </nav>

          {/* Hero */}
          <header className="mb-12 md:mb-16">
            <p className="text-purple-400 uppercase text-xs tracking-widest mb-4 font-semibold">
              Real Output Example · {example.sourcePodcast}
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-unbounded font-bold leading-[1.05] mb-6">
              {example.title}
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-3xl mb-8">
              {example.intro}
            </p>

            {/* Source episode meta */}
            <div className="inline-flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4 rounded-xl bg-neutral-900/60 border border-neutral-800">
              {Object.entries(example.meta).map(([k, v], i, arr) => (
                <span key={k} className="flex items-center gap-3">
                  <span className="text-xs text-neutral-500 uppercase tracking-wider">
                    {k}
                  </span>
                  <span className="text-sm text-white font-medium">{v}</span>
                  {i < arr.length - 1 && (
                    <span className="text-neutral-700">·</span>
                  )}
                </span>
              ))}
            </div>
          </header>

          {/* Primary CTAs */}
          <section className="mb-14 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <button
                className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3.5"
                data-testid="detail-try-free-cta"
              >
                Try Your First Episode Free
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <a
              href={example.pdfUrl}
              download
              className="inline-flex items-center gap-2 text-sm font-medium text-white border border-neutral-700 hover:border-purple-500/60 rounded-full px-5 py-3 transition-colors"
              data-testid="detail-download-pdf"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
            <span className="text-xs text-neutral-500 italic">
              Generated from a real uploaded episode
            </span>
          </section>

          {/* Preview gallery */}
          <section
            aria-labelledby="gallery-heading"
            className="mb-16 md:mb-20"
          >
            <h2
              id="gallery-heading"
              className="text-2xl md:text-3xl font-unbounded font-semibold mb-2"
            >
              Inspect the deliverable
            </h2>
            <p className="text-neutral-400 mb-8">
              Selected spreads from the generated PDF. Click any preview to view
              full-size.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {example.previews.map((p, i) => (
                <button
                  key={p.src}
                  type="button"
                  onClick={() => setLightbox(p)}
                  className="group text-left rounded-xl overflow-hidden bg-[#0A0913] border border-neutral-800 hover:border-purple-500/50 transition-colors"
                  data-testid={`preview-thumb-${i}`}
                >
                  <div className="aspect-[1191/1684] overflow-hidden bg-[#0A0913]">
                    <img
                      src={p.src}
                      alt={p.alt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                  <p className="px-4 py-3 text-sm text-neutral-300">
                    {p.caption}
                  </p>
                </button>
              ))}
            </div>
          </section>

          {/* What's included */}
          <section
            aria-labelledby="included-heading"
            className="mb-16 md:mb-20"
          >
            <h2
              id="included-heading"
              className="text-2xl md:text-3xl font-unbounded font-semibold mb-2"
            >
              What's included
            </h2>
            <p className="text-neutral-400 mb-8 max-w-2xl">
              Every output is generated automatically and delivered inside the
              EpisodeKit dashboard along with this PDF.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {example.includedOutputs.map((o) => (
                <li
                  key={o}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-neutral-900/50 border border-neutral-800"
                >
                  <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  <span className="text-sm md:text-base text-white">{o}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Why this matters */}
          <section
            aria-labelledby="why-heading"
            className="mb-16 md:mb-20"
          >
            <h2
              id="why-heading"
              className="text-2xl md:text-3xl font-unbounded font-semibold mb-2"
            >
              Why this matters
            </h2>
            <p className="text-neutral-400 mb-8 max-w-2xl">
              One uploaded episode replaces an entire content team — built for
              ranking, discoverability, and creator workflow speed.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {example.why.map((w) => (
                <div
                  key={w.title}
                  className="p-6 rounded-2xl bg-gradient-to-b from-[#15131F] to-[#0E0D17] border border-neutral-800"
                >
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {w.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed text-sm">
                    {w.body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="text-center max-w-2xl mx-auto pt-8 border-t border-neutral-800/60">
            <h2 className="text-3xl md:text-4xl font-unbounded font-bold mb-4 mt-12">
              This entire system came from one upload.
            </h2>
            <p className="text-neutral-400 mb-7">
              Yours will too. Upload once, publish everywhere.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/register">
                <button
                  className="btn-primary inline-flex items-center gap-2 text-base px-8 py-4"
                  data-testid="detail-final-cta"
                >
                  Try Your First Episode Free
                  <ArrowRight className="w-5 h-5" />
                </button>
              </Link>
              <Link
                to="/episode-examples"
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-700 hover:border-purple-500/60 rounded-full px-5 py-3 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all examples
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
          data-testid="preview-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close preview"
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-full max-h-full rounded-lg shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
